import React, { useMemo, useState } from 'react';
import { Group, Switch, Text } from '@mantine/core';
import { MantineReactTable } from 'mantine-react-table';
import type { MRT_Row, MRT_ColumnDef, MRT_RowSelectionState, MRT_SortingState, MRT_ColumnFiltersState, MRT_ExpandedState } from 'mantine-react-table';
import IonIcon from '@reacticons/ionicons';
import ExportToCsv from '../ExportToCsv/ExportToCsv';
import { CustomLoaderPage } from '../loader/loader-page';

// Rows without an `id` fall back to their index. A bare String(row.id) turns
// every id-less row into "undefined", which MRT uses as the React key, so every
// row collides — and selecting or expanding one row selects or expands them
// all. The fallback lives in its own namespace, so an id-less row at index 0
// never shares an id with a row whose own id is '0'.
const FALLBACK_ROW_ID = '__row:'
export const getMainTableRowId = (row: Record<string, any>, index: number, parentRow?: any): string => {
  if (row.id != null && row.id !== '') return String(row.id)
  return parentRow ? `${parentRow.id}.${FALLBACK_ROW_ID}${index}` : `${FALLBACK_ROW_ID}${index}`
}

type Props<T extends Record<string, T>> = {
  stretch?: boolean
  maxHeight?: string
  tableName: string
  csvExportEnabled?: boolean
  columnDefs: MRT_ColumnDef<T>[]
  data?: T[]
  setRow: (e: any, z: string) => any
  enableColumnOrdering?: boolean
  enableColumnResizing?: boolean
  enableGrouping?: boolean
  enableColumnFilters?: boolean
  enableColumnActions?: boolean
  enableFilters?: boolean
  enableSorting?: boolean
  enableFullScreenToggle?: boolean
  enableDensityToggle?: boolean
  enableHiding?: boolean
  enableTopToolbar?: boolean
  minHeight?: string
  overflow?: string
  withPagination?: boolean
  headerBackgroundColor?: string
  isLoading?: boolean
  rowsPerPage?: number
  border?: boolean
  controlSectionBorder?: boolean
  renderDetailPanel?: (row: any) => React.ReactNode; // Add renderDetailPanel prop
  isAccordionTable?: boolean
  /**
   * When true, only one detail panel can be expanded at a time — expanding a
   * new row collapses any other open one. Opt-in to preserve existing
   * multi-expand behavior for tables that don't pass this prop.
   */
  singleExpand?: boolean
  showBottomBoolean?: boolean
  isInactiveLoanLocs?: boolean
  fromTradeEntry?: boolean
  onToggle?: (isActive: boolean) => void;
  disableStriping?: boolean;
  customExportData?: any[];
  enableMultiRowSelection?: boolean;
  hiddenColumns?: string[];
  selectedRows?: (selectedData: any, cellName: string) => void;
  tableSelectedRows?: Record<string, boolean>
  setTableSelectedRows?: React.Dispatch<React.SetStateAction<MRT_RowSelectionState>>
  fromWireInstructions?: boolean
  enableRowSelection?: boolean;
  headerVerticalAlign?: 'top' | 'middle' | 'bottom';
  initialSorting?: MRT_SortingState
  onSortingChange?: (updaterOrValue: MRT_SortingState | ((old: MRT_SortingState) => MRT_SortingState)) => void
  initialColumnFilters?: MRT_ColumnFiltersState
  onColumnFiltersChange?: (updaterOrValue: MRT_ColumnFiltersState | ((old: MRT_ColumnFiltersState) => MRT_ColumnFiltersState)) => void
  initialPagination?: { pageSize: number; pageIndex: number }
  onPaginationChange?: (updaterOrValue: { pageSize: number; pageIndex: number } | ((old: { pageSize: number; pageIndex: number }) => { pageSize: number; pageIndex: number })) => void
  initialColumnVisibility?: Record<string, boolean>
  onColumnVisibilityChange?: (updaterOrValue: Record<string, boolean> | ((old: Record<string, boolean>) => Record<string, boolean>)) => void
  selectedRowId?: string | null
  highlightRow?: (row: MRT_Row<T>) => string | undefined
}

export default function MainTable<
  T extends Record<string, any>
>({
  tableName,
  maxHeight,
  csvExportEnabled,
  columnDefs,
  data = [],
  setRow,
  enableColumnOrdering,
  enableColumnResizing,
  enableGrouping,
  enableColumnFilters,
  enableColumnActions,
  enableFilters,
  enableSorting,
  enableFullScreenToggle,
  enableDensityToggle,
  enableHiding,
  enableTopToolbar,
  minHeight,
  overflow,
  headerBackgroundColor,
  withPagination = true,
  isLoading = false,
  border = false,
  controlSectionBorder = true,
  rowsPerPage,
  renderDetailPanel,
  isAccordionTable,
  singleExpand = false,
  showBottomBoolean,
  isInactiveLoanLocs,
  fromTradeEntry,
  onToggle,
  disableStriping = false,
  customExportData = [],
  enableMultiRowSelection,
  selectedRows,
  stretch = false,
  hiddenColumns = [],
  tableSelectedRows = {},
  setTableSelectedRows,
  fromWireInstructions = false,
  enableRowSelection,
  headerVerticalAlign,
  initialSorting,
  onSortingChange,
  initialColumnFilters,
  onColumnFiltersChange,
  initialPagination,
  onPaginationChange,
  initialColumnVisibility,
  highlightRow,
  onColumnVisibilityChange,
  selectedRowId,
}: Props<T>) {
  const columns = useMemo<MRT_ColumnDef<T>[]>(() => columnDefs, [])

  const [sorting, setSorting] = useState<MRT_SortingState>(initialSorting || [])
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(initialColumnFilters || [])
  const [pagination, setPagination] = useState<{ pageSize: number; pageIndex: number }>(
    initialPagination || { pageSize: rowsPerPage ?? 25, pageIndex: 0 }
  )

  // Single-expand state: only used when singleExpand is true. We control
  // the table's expanded state so we can collapse the previously open row
  // whenever a new one is expanded.
  const [expanded, setExpanded] = useState<MRT_ExpandedState>({})

  const handleExpandedChange = (
    updaterOrValue: MRT_ExpandedState | ((old: MRT_ExpandedState) => MRT_ExpandedState),
  ) => {
    setExpanded((old: MRT_ExpandedState) => {
      const next = typeof updaterOrValue === 'function' ? updaterOrValue(old) : updaterOrValue
      // Defensive: if MRT ever passes `true` (expand-all), don't try to dedupe.
      if (next === true) return next
      const oldKeys = old === true ? [] : Object.keys(old).filter((k) => (old as Record<string, boolean>)[k])
      const nextKeys = Object.keys(next).filter((k) => (next as Record<string, boolean>)[k])
      const added = nextKeys.filter((k) => !oldKeys.includes(k))
      // A new row was just expanded: keep only that one open.
      if (added.length > 0) {
        return { [added[added.length - 1]]: true }
      }
      return next
    })
  }

  // Merge initial column visibility with hidden columns
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => {
    const initial = initialColumnVisibility || {}
    const permanentlyHidden = (hiddenColumns || []).reduce((acc, colKey) => {
      acc[colKey] = false
      return acc
    }, {} as Record<string, boolean>)
    return { ...initial, ...permanentlyHidden }
  })

  // When selection changes, extract full row data
  const handleRowSelectionChange = (updaterOrValue: MRT_RowSelectionState | ((old: MRT_RowSelectionState) => MRT_RowSelectionState)) => {
    if (!setTableSelectedRows) return;
    setTableSelectedRows((oldSelection: MRT_RowSelectionState) => {
      const newSelection = typeof updaterOrValue === 'function' ? updaterOrValue(oldSelection) : updaterOrValue;
      const selectedRowIds = Object.keys(newSelection).filter((id) => newSelection[id]);
      const selectedData = selectedRowIds
        .map((id) => data.find((row, index) => getMainTableRowId(row, index) === id))
        .filter(Boolean);
      selectedRows?.(selectedData, 'tableSelectedRows');
      return newSelection;
    });
  };

  // Handle sorting changes
  const handleSortingChange = (updaterOrValue: MRT_SortingState | ((old: MRT_SortingState) => MRT_SortingState)) => {
    setSorting((oldSorting: MRT_SortingState) => {
      const newSorting = typeof updaterOrValue === 'function' ? updaterOrValue(oldSorting) : updaterOrValue;
      onSortingChange?.(newSorting);
      return newSorting;
    });
  };

  // Handle column filter changes
  const handleColumnFiltersChange = (updaterOrValue: MRT_ColumnFiltersState | ((old: MRT_ColumnFiltersState) => MRT_ColumnFiltersState)) => {
    setColumnFilters((oldFilters: MRT_ColumnFiltersState) => {
      const newFilters = typeof updaterOrValue === 'function' ? updaterOrValue(oldFilters) : updaterOrValue;
      onColumnFiltersChange?.(newFilters);
      return newFilters;
    });
  };

  // Handle pagination changes
  const handlePaginationChange = (updaterOrValue: { pageSize: number; pageIndex: number } | ((old: { pageSize: number; pageIndex: number }) => { pageSize: number; pageIndex: number })) => {
    setPagination((oldPagination: { pageSize: number; pageIndex: number }) => {
      const newPagination = typeof updaterOrValue === 'function' ? updaterOrValue(oldPagination) : updaterOrValue;
      onPaginationChange?.(newPagination);
      return newPagination;
    });
  };

  // Handle column visibility changes
  const handleColumnVisibilityChange = (updaterOrValue: Record<string, boolean> | ((old: Record<string, boolean>) => Record<string, boolean>)) => {
    setColumnVisibility((oldVisibility: Record<string, boolean>) => {
      const newVisibility = typeof updaterOrValue === 'function' ? updaterOrValue(oldVisibility) : updaterOrValue;
      onColumnVisibilityChange?.(newVisibility);
      return newVisibility;
    });
  };

  const exportData = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }

    return data.map((row) => {
      const formattedRow = columns.map((col) => {
        const value = row[col.accessorKey as keyof T];
        const key = col.header === 'Actions' ? col.accessorKey : col.header;

        return {
          [`${key}`]: value
        };
      }).reduce((acc, curr) => ({ ...acc, ...curr }), {});

      return formattedRow;
    });
  }, [data, columns]);

  if (isLoading) {
    return <CustomLoaderPage />
  }

  return (
    <Group sx={{
      overflowX: 'auto',
      maxWidth: '100%',
      width: stretch ? '100%' : undefined,
    }}>
      <MantineReactTable
        mantineTableHeadCellProps={{
          sx: {
            backgroundColor: headerBackgroundColor ? headerBackgroundColor : 'none', // Set the background color of header cells to red
            ...(headerVerticalAlign && { verticalAlign: headerVerticalAlign }),
            // Hide divider if this is the last column
            '&:last-of-type .mantine-Divider-vertical': {
              display: 'none',
            },
          }
        }}
        defaultColumn={{
          maxSize: 400,
          minSize: 20,
          size: 150,
        }}
        enableColumnResizing={enableColumnResizing ?? true}
        columns={columns}
        data={data ?? []}
        mantineTableBodyProps={{
          sx: {
            ...(disableStriping
              ? {} // No striping styles
              : {
                //stripe the rows, make odd rows a darker color
                '& tr:nth-of-type(odd)': {
                  backgroundColor: '#f5f5f5',
                  // Ensure no divider for the last column's body cell too
                  '&:last-of-type .mantine-Divider-vertical': {
                    display: 'none',
                  },
                },
              }),
            '& tr td:first-of-type': (isAccordionTable ? {
              width: fromTradeEntry ? '30px' : '10px',
              maxWidth: fromTradeEntry ? '30px' : '10px',
              minWidth: fromTradeEntry ? '30px' : '10px',
              textAlign: 'center', // Center the icon
            } : {}),
          },
        }}
        enableColumnOrdering={enableColumnOrdering ?? true}
        enableGrouping={enableGrouping ?? true}
        enableColumnFilters={enableColumnFilters ?? true}
        enableColumnActions={enableColumnActions ?? true}
        enableFilters={enableFilters ?? true}
        enableSorting={enableSorting ?? true}
        enableFullScreenToggle={enableFullScreenToggle ?? true}
        enableStickyHeader
        enableDensityToggle={enableDensityToggle ?? true}
        enableHiding={enableHiding ?? true}
        enableTopToolbar={enableTopToolbar ?? true}
        renderDetailPanel={renderDetailPanel}
        enableMultiRowSelection={enableMultiRowSelection ?? false}
        enableRowSelection={enableRowSelection ?? false}
        getRowId={getMainTableRowId} //Ensure row IDs match
        onRowSelectionChange={handleRowSelectionChange} // Track selection
        onSortingChange={onSortingChange ? handleSortingChange : undefined}
        onColumnFiltersChange={onColumnFiltersChange ? handleColumnFiltersChange : undefined}
        onPaginationChange={handlePaginationChange}
        onColumnVisibilityChange={onColumnVisibilityChange ? handleColumnVisibilityChange : undefined}
        {...(singleExpand && { onExpandedChange: handleExpandedChange })}
        state={{
          rowSelection: tableSelectedRows,
          columnVisibility,
          sorting,
          columnFilters,
          pagination,
          ...(singleExpand ? { expanded } : {}),
        }}

        displayColumnDefOptions={
          enableRowSelection
            ? {
              'mrt-row-select': {
                header: '',   // blank out the text
                size: 10,     // tighten up the width
              },
            }
            : undefined
        }

        mantineTableProps={{
          sx: () => ({
            ...(enableMultiRowSelection && {
              ...(isAccordionTable ? {
                // For accordion tables: first column is expand/collapse, second is checkbox
                '& thead tr th:nth-of-type(2)': {
                  width: '25px', // Checkbox column width
                  maxWidth: '25px',
                  minWidth: '25px',
                  paddingRight: '0px',
                },
                '& tbody tr td:nth-of-type(2)': {
                  width: '25px', // Checkbox column width
                  maxWidth: '25px',
                  minWidth: '25px',
                  paddingRight: '0px',
                },
              } : {
                // For non-accordion tables: first column is checkbox
                '& thead tr th:first-of-type': {
                  width: '25px', // Checkbox column width
                  maxWidth: '25px',
                  minWidth: '25px',
                  paddingRight: '0px',
                },
                '& tbody tr td:first-of-type': {
                  width: '25px', // Checkbox column width
                  maxWidth: '25px',
                  minWidth: '25px',
                  paddingRight: '0px',
                },
              }),
            }),
            ...(controlSectionBorder === false && {
              border: 'none',
            }),
          }),
        }}

        mantineDetailPanelProps={{ sx: { paddingLeft: '40px !important', paddingRight: '40px !important' } }}
        mantineTableHeadProps={
          isAccordionTable
            ? {
              sx: {
                '& tr th:first-of-type': {
                  width: '10px', // Expand/collapse column width
                  maxWidth: '10px',
                  minWidth: '10px',
                  textAlign: 'center',
                },
              },
            }
            : {}
        }
        mantineExpandButtonProps={({ row }) =>
          isAccordionTable
            ? {
              children: row.getIsExpanded() ? (
                <IonIcon name="chevron-down-outline" style={{ fontSize: '16px', color: '#32439A' }} />
              ) : (
                <IonIcon name="chevron-forward-outline" style={{ fontSize: '16px', color: '#32439A' }} />
              ),
            }
            : {}
        }
        mantineTableBodyRowProps={({ row }) => ({
          sx: (theme) => {
            const isExpanded = row.getIsExpanded();
            const isSelected = row.getIsSelected() || (selectedRowId != null && String(row.original.id) === selectedRowId);
            const highlight = highlightRow?.(row);
            const baseColor = isSelected
              ? '#228be633'
              : isExpanded
                ? '#CAD2FF'
                : highlight ?? 'transparent';
            // Selected + hover: darker selected shade for feedback. Highlighted
            // rows (e.g. pink-tinted held trades) deepen on hover the same way.
            const hoverColor = isSelected
              ? '#228be666'
              : isExpanded
                ? '#B0BCFF'
                : highlight
                  ? '#F7DDDC'
                  : theme.colors.gray[1];

            return {
              backgroundColor: baseColor,

              '& > td': {
                backgroundColor: `${baseColor} !important`,
              },

              '&:hover': {
                backgroundColor: hoverColor,
              },

              '&:hover > td': {
                backgroundColor: `${hoverColor} !important`,
              },
            };
          },
        })}
        mantinePaperProps={{
          withBorder: controlSectionBorder === false ? false : true, // disable the data-with-border attribute
          style: controlSectionBorder === false ? { border: 'none' } : { border: '0.0625rem solid #dee2E6' }, // double check there's no fallback
        }}
        mantineTableContainerProps={{
          sx: maxHeight ? {
            border: border ? '1px solid #71C0EE' : undefined,
            overflow: overflow ?? 'auto',
            minHeight: minHeight ?? '250px',
            maxWidth: '100%',
            maxHeight: maxHeight,
          } : {
            border: border ? '1px solid #71C0EE' : undefined,
            overflow: overflow ?? 'auto',
            minHeight: minHeight ?? '250px',
            maxWidth: '100%',
          },
        }}
        mantineBottomToolbarProps={{
          mih: "5.5rem",
          sx: {
            backgroundColor: '#f7f7f7',
          }
        }}
        renderBottomToolbarCustomActions={() => (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {csvExportEnabled && (
              <ExportToCsv
                csvName={tableName}
                exportObj={customExportData.length > 0 ? customExportData : exportData}
                style={{ width: '150px', height: '35px', backgroundColor: '#32439A', color: '#fff' }}
              />
            )}
            {showBottomBoolean && (
              <Group align="center" style={{ marginLeft: '10px' }}>
                <Switch
                  label={
                    <Text className={`status-switch-label ${isInactiveLoanLocs ? 'active' : ''}`}>
                      {isInactiveLoanLocs ? 'Show Active' : 'Show Inactive'}
                    </Text>
                  }
                  checked={isInactiveLoanLocs}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => onToggle?.(e.currentTarget.checked)}
                  size="md"
                  classNames={{
                    track: `status-switch-track ${isInactiveLoanLocs ? 'active' : ''}`,
                    thumb: `status-switch-thumb ${isInactiveLoanLocs ? 'active' : 'inactive'}`,
                  }}
                />
              </Group>
            )}
          </div>
        )}
        mantineTopToolbarProps={{
          sx: {
            maxWidth: '100%',
          }
        }}
        mantineFilterDateInputProps={{
          firstDayOfWeek: 0,
        }}
        mantineTableBodyCellProps={({ cell, row }) => ({
          onClick: () => {
            setRow(row.original, cell.column.id)
          },
        })}
        icons={{
          IconDotsVertical: () => <IonIcon name='funnel-outline' />,
        }}
        // PAGINATION
        enableBottomToolbar={withPagination}
        enablePagination={withPagination}
        autoResetPageIndex={false}
        paginationDisplayMode='pages'
        mantinePaginationProps={{
          rowsPerPageOptions: ["5", "25", "50", "100"],
          size: 'xl',
          radius: 'xl',
          siblings: 1,
          withEdges: false,
        }}

        {...(fromWireInstructions && {
          enableExpanding: true,
          enableExpandAll: true,
          groupedColumnMode: false,
          positionExpandColumn: 'first',
        })}

        /* initialState depends on fromWireInstructions */
        initialState={
          fromWireInstructions
            ? {
              grouping: ['currency'],
              pagination: { pageSize: rowsPerPage ?? 25, pageIndex: 0 },
            }
            : {
              pagination: { pageSize: rowsPerPage ?? 25, pageIndex: 0 },
            }
        }
      />
    </Group>
  )
}
